import type { PendingConnectorWrite } from "@/lib/connectorPendingWrite";
import type { CloudTaskRecord, CloudTaskList, PeopleTaskRequest } from "../../supabase/functions/_shared/peopleTasksDomain";
import { cloudTaskId } from "../../supabase/functions/_shared/peopleTasksDomain";
export type { CloudTaskRecord, CloudTaskList } from "../../supabase/functions/_shared/peopleTasksDomain";

export type ExternalTaskProvider = "google_tasks" | "microsoft_todo";
export type ExternalTaskWriteAction = "create" | "update" | "complete" | "reopen" | "delete";
export type ExternalTaskAction = "search" | ExternalTaskWriteAction;
export type ExternalTaskRequest = PeopleTaskRequest & { provider: ExternalTaskProvider; mirror_context?: { session_id: string; action_id: string } };
export interface ExternalTaskResponse extends Record<string, unknown> {
  ok: true; provider: ExternalTaskProvider; action: PeopleTaskRequest["action"];
  item?: CloudTaskRecord; items?: CloudTaskRecord[]; lists?: CloudTaskList[]; next_cursor?: string | null;
  complete?: boolean; has_more?: boolean;
}
export interface ExternalTaskActionPrefill {
  kind?: "task";
  provider: ExternalTaskProvider | "auto";
  action: ExternalTaskAction;
  action_id: string;
  query?: string;
  title?: string;
  notes?: string;
  due_date?: string | null;
  list_id?: string;
  item_id?: string;
  expected_user_id?: string;
  expected_session_id?: string;
}

/** A receipt locator. The server verifies provider evidence independently. */
export interface MirrorExternalTaskReceiptReference {
  provider: ExternalTaskProvider;
  action: ExternalTaskWriteAction;
  idempotency_key: string;
  action_id: string;
  userId: string;
  sessionId: string;
}
export interface MirrorExternalTaskOrigin { userId: string; sessionId: string; actionId: string }
const object = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);
const providerId = (value: unknown) => { try { return cloudTaskId(value) === value; } catch { return false; } };
export function matchesMirrorExternalTaskReceiptOrigin(value: unknown, origin: MirrorExternalTaskOrigin): value is MirrorExternalTaskReceiptReference {
  return object(value) && !!origin.userId && !!origin.sessionId && !!origin.actionId && value.userId === origin.userId &&
    value.sessionId === origin.sessionId && value.action_id === origin.actionId &&
    ["google_tasks", "microsoft_todo"].includes(String(value.provider)) &&
    ["create", "update", "complete", "reopen", "delete"].includes(String(value.action)) &&
    value.idempotency_key === "mirror_external_task_" + origin.actionId + "_" + value.action + "_0";
}
export function mirrorExternalTaskReceiptReference(entry: PendingConnectorWrite, origin: MirrorExternalTaskOrigin): MirrorExternalTaskReceiptReference | null {
  const body = entry.body;
  const reference = { provider: body.provider, action: body.action, idempotency_key: body.idempotency_key,
    action_id: origin.actionId, userId: origin.userId, sessionId: origin.sessionId };
  if (entry.group !== "external_task" || entry.key !== origin.userId + ":external_task" || entry.ownerId !== origin.userId ||
    entry.status !== "succeeded" || !object(body.mirror_context) || body.mirror_context.session_id !== origin.sessionId ||
    body.mirror_context.action_id !== origin.actionId || !matchesMirrorExternalTaskReceiptOrigin(reference, origin)) return null;
  return reference;
}

export class ExternalTaskExecutorError extends Error {
  readonly safe_retry: boolean;
  constructor(code: string, public context?: Response, safeRetry = false) {
    super(code); this.name = "ExternalTaskExecutorError"; this.safe_retry = safeRetry;
  }
}
export async function externalTaskOwnerSession(ownerId: string) {
  const { supabase } = await import("@/integrations/supabase/client");
  const user = await supabase.auth.getUser();
  if (user.error || user.data.user?.id !== ownerId) throw new ExternalTaskExecutorError("invalid_session");
  const session = await supabase.auth.getSession();
  if (session.error || session.data.session?.user.id !== ownerId || !session.data.session.access_token) throw new ExternalTaskExecutorError("invalid_session");
  return session.data.session;
}

/** Explicit consent has its own owner token and never dispatches a task draft. */
export async function enableExternalTaskChanges(provider: ExternalTaskProvider, ownerId: string, isCurrent: () => boolean): Promise<void> {
  const [{ supabase }, { platformOpen }] = await Promise.all([
    import("@/integrations/supabase/client"), import("@/lib/platformOpen"),
  ]);
  if (!isCurrent()) throw new ExternalTaskExecutorError("invalid_session");
  const session = await externalTaskOwnerSession(ownerId);
  if (!isCurrent()) throw new ExternalTaskExecutorError("invalid_session");
  const response = await supabase.functions.invoke("people-tasks-oauth-init", {
    body: { provider, write_access: true }, headers: { Authorization: "Bearer " + session.access_token },
  });
  await externalTaskOwnerSession(ownerId);
  if (!isCurrent()) throw new ExternalTaskExecutorError("invalid_session");
  if (response.error || typeof response.data?.authorize_url !== "string") throw new ExternalTaskExecutorError("connection_failed");
  let url: URL;
  try { url = new URL(response.data.authorize_url); } catch { throw new ExternalTaskExecutorError("connection_failed"); }
  if (url.protocol !== "https:") throw new ExternalTaskExecutorError("connection_failed");
  platformOpen(url.href);
}

export function parseCloudTaskRecord(value: unknown): CloudTaskRecord | null {
  if (!object(value) || !["id", "list_id", "title", "notes", "version", "status"].every(key => typeof value[key] === "string") ||
    !providerId(value.id) || !providerId(value.list_id) ||
    !["completed", "read_only", "writable", "delete_writable", "content_truncated", "assigned", "recurrence_known"].every(key => typeof value[key] === "boolean") ||
    !(value.recurring === null || typeof value.recurring === "boolean") ||
    !["scheduled_date", "due_date_time"].includes(String(value.due_kind)) ||
    !(value.parent_id === null || typeof value.parent_id === "string") ||
    !["due_date", "due_date_time", "due_time_zone"].every(key => value[key] === null || typeof value[key] === "string") ||
    !Array.isArray(value.write_actions) || value.write_actions.some(action => !["update", "complete", "reopen"].includes(String(action)))) return null;
  return value as unknown as CloudTaskRecord;
}
export function parseCloudTaskList(value: unknown): CloudTaskList | null {
  if (!object(value) || !providerId(value.id) || typeof value.title !== "string" ||
    typeof value.read_only !== "boolean" || typeof value.create_writable !== "boolean" || typeof value.content_truncated !== "boolean") return null;
  return value as unknown as CloudTaskList;
}

/** Read results use a pinned owner token and recheck the account after transport. */
export async function invokeExternalTask(body: ExternalTaskRequest, ownerId: string): Promise<ExternalTaskResponse> {
  const session = await externalTaskOwnerSession(ownerId);
  const { supabase } = await import("@/integrations/supabase/client");
  const response = await supabase.functions.invoke("people-tasks-executor", { body: { ...body, mutation_contract: 2 }, headers: { Authorization: "Bearer " + session.access_token } });
  await externalTaskOwnerSession(ownerId);
  if (response.error) {
    const context = (response.error as { context?: Response }).context;
    if (context && typeof context.clone === "function") {
      try { const payload = await context.clone().json();
        if (typeof payload?.error === "string") throw new ExternalTaskExecutorError(payload.error, context, payload.safe_retry === true);
      } catch (cause) { if (cause instanceof ExternalTaskExecutorError) throw cause; }
    }
    throw new ExternalTaskExecutorError("outcome_unknown", context);
  }
  if (typeof response.data?.error === "string") throw new ExternalTaskExecutorError(response.data.error, undefined, response.data.safe_retry === true);
  if (!object(response.data) || response.data.ok !== true || response.data.provider !== body.provider || response.data.action !== body.action)
    throw new ExternalTaskExecutorError("outcome_unknown");
  return response.data as ExternalTaskResponse;
}

/** Fetch the current owned local row; receipt snapshots never replace newer local metadata. */
export async function refreshExternalTaskLocalCopy(entry: PendingConnectorWrite, ownerId: string, isCurrent: () => boolean): Promise<boolean> {
  const [{ publishTaskChange, publishTaskRefresh }, { supabase }, { buildMirrorTaskSnapshot, MIRROR_TASK_SELECT_FIELDS }, { decodeGoogleTaskExternalId }] = await Promise.all([
    import("@/lib/taskChanges"), import("@/integrations/supabase/client"),
    import("../../supabase/functions/_shared/mirrorTaskActions"), import("../../supabase/functions/_shared/googleTaskSync"),
  ]);
  await externalTaskOwnerSession(ownerId);
  if (!isCurrent()) return false;
  const result = object(entry.confirmedResult) && object(entry.confirmedResult.result) ? entry.confirmedResult.result : null;
  const sync = result && object(result.local_sync) ? result.local_sync : null;
  if (entry.body.provider === "microsoft_todo") {
    const {refreshObservedTaskLocalCopy} = await import("@/lib/externalTaskLocalCopy");
    return refreshObservedTaskLocalCopy({owner:ownerId,provider:"microsoft_todo",listId:typeof entry.body.list_id==="string"?entry.body.list_id:undefined,
      itemId:typeof result?.external_id==="string"?result.external_id:"",action:entry.body.action,
      sync:sync as {status?:string;change?:string|null;task_id?:string;external_id?:string}|undefined,isCurrent});
  }
  if (entry.body.provider !== "google_tasks") return false;
  if (sync?.status === "ok" && typeof sync.task_id === "string" && result && typeof result.external_id === "string") {
    try {
      const read = await supabase.from("tasks").select(MIRROR_TASK_SELECT_FIELDS).eq("id", sync.task_id).eq("user_id", ownerId).eq("source", "google_tasks").maybeSingle();
      await externalTaskOwnerSession(ownerId);
      if (!isCurrent()) return false;
      const task = !read.error ? buildMirrorTaskSnapshot(read.data, ownerId) : null;
      const identity = task ? decodeGoogleTaskExternalId(task.external_id) : null;
      if (task && identity?.itemId === result.external_id && identity.listId === entry.body.list_id) {
        publishTaskChange({ userId: ownerId, id: sync.task_id, task: task as unknown as import("@/hooks/useTasks").Task });
        return true;
      }
    } catch (cause) { if (cause instanceof ExternalTaskExecutorError) throw cause;
      console.warn("[ExternalTaskActionModal] local refresh failed", cause); }
  }
  await externalTaskOwnerSession(ownerId);
  if (isCurrent()) publishTaskRefresh(ownerId);
  return false;
}
